import { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Box, Divider, Modal, Text, Typography } from '@percent/lemonade'
import {
  ErrorView,
  Loader,
  PercentButton,
  FieldInput,
  UpdateModalInformation
} from '@percent/admin-dashboard/common/components'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { ReactComponent as CloseIcon } from '@percent/admin-dashboard/common/assets/images/close.svg'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { PatchOrganisationParams } from '@percent/admin-dashboard/api/types'
import styles from './UpdateWebsiteModal.module.scss'
import { UpdateWebsiteModalProps } from './UpdateWebsiteModal.types'

export function UpdateWebsiteModal({ validation, refresh, isModalOpened, setModalOpened }: UpdateWebsiteModalProps) {
  const { t } = useTranslation()
  const [isSent, setIsSent] = useState(false)
  const { adminService } = useServices()
  const navigate = useNavigate()

  const { values, setFieldValue, handleBlur, touched, errors, dirty, isValid } = useFormik({
    initialValues: {
      website: validation?.websiteVerifiedAt ? validation?.verifiedWebsite ?? validation?.website : validation?.website
    },
    validationSchema: () =>
      yup.object().shape({
        website: yup.string().required('Required').url(t('errorMessage.validWebsite')).max(255).nullable()
      }),
    onSubmit: () => {}
  })

  const [{ isLoading, errorMessage }, { apiFunc: approveFunc }] = useMutation(
    adminService.editValidationRequest,
    () => {
      setIsSent(true)
    }
  )

  const handleUpdate = () => {
    const payload = validation?.websiteVerifiedAt
      ? { verifiedWebsite: values?.website as string }
      : { website: values?.website }

    approveFunc({ id: validation!.id, payload } as PatchOrganisationParams)
  }

  const handleCloseModalAfterUpdate = () => {
    refresh()
    setModalOpened(false)
    setIsSent(false)
    navigate(`/validations/${validation!.id}`)
  }

  return (
    <Modal open={isModalOpened} onClose={() => setModalOpened(false)}>
      {isSent ? (
        <UpdateModalInformation
          handleClick={handleCloseModalAfterUpdate}
          setModalOpened={setModalOpened}
          title={t('dialog.updateWebsite.websiteUpdated')}
          subtitle={<Typography variant="large">{t('dialog.updateWebsite.websiteStored')}</Typography>}
          buttonTitle={t('button.goBackToDetails')}
        />
      ) : (
        <Box className={styles.wrapper}>
          <Box className={styles.titleWrapper}>
            <Text className={styles.title}>
              {t(`dialog.updateWebsite.${validation?.website ? 'editWebsite' : 'addWebsite'}`)}
            </Text>
            <CloseIcon onClick={() => setModalOpened(false)} />
          </Box>
          <Divider className={styles.divider} />
          <Box className={styles.textWrapper}>
            <Typography variant="large" className={styles.bodyText}>
              {t('dialog.updateWebsite.body')}
            </Typography>
          </Box>
          <FieldInput
            name="website"
            label={t('form.nonprofitWebsite')}
            placeHolder={t('form.website')}
            onChange={(value: string) => setFieldValue('website', value)}
            value={values.website}
            handleBlur={handleBlur}
            error={errors.website}
            touched={touched.website}
          />
          {errorMessage && <ErrorView errorMessage={errorMessage} />}
          {isLoading ? (
            <Loader />
          ) : (
            <Box className={styles.buttonWrapper}>
              <PercentButton title={t('button.saveWebsite')} onClick={handleUpdate} disabled={!(isValid && dirty)} />
              <PercentButton
                title={t('button.cancel')}
                onClick={() => setModalOpened(false)}
                textColour="secondaryTextColour"
                outlined
              />
            </Box>
          )}
        </Box>
      )}
    </Modal>
  )
}
